import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link } from 'react-router';
import cn from 'classnames';

import { HYDRA_ZEN_RETURNS } from 'constants/hydraTests';

import { isAssigned, triggerAssignment } from 'actions/ab';
import { pluralize } from 'helpers';
import { isOrderReturnable } from 'helpers/MyAccountUtils';
import SiteAwareMetadata from 'components/SiteAwareMetadata';
import { PageLoader } from 'components/Loader';
import ReturnProductCards from 'components/account/Returns/ReturnProductCards';
import FloatingReturnItemList from 'components/account/Returns/FloatingReturnItemList';
import { fetchOrders } from 'actions/account/orders';
import {
  removeItemForCancellationOrReturn,
  selectItemForCancellationOrReturn
} from 'actions/account/account';
import {
  resetReturnsInState,
  stepSelection,
  updateBoxNumInfo,
  updateItemInBoxInfo
} from 'actions/account/returns';
import { cols, container, leftCol, rightCol } from 'styles/containers/returns/returns.scss';
import css from 'styles/containers/account/returnAll.scss';

const RETURN_MAX_BOXES = 5;

export class ReturnAll extends Component {
  static contextTypes = {
    router: PropTypes.object.isRequired,
    marketplace: PropTypes.object
  }

  state = {
    boxNum: 1 // default to 1 box
  };

  componentDidMount() {
    const {
      account: { cancelOrReturnItems: items },
      orders: { orders },
      returns: { boxInfo },
      fetchOrders,
      updateBoxNumInfo
    } = this.props;

    const { marketplace: { account: { martyReturns } } } = this.context;

    triggerAssignment(HYDRA_ZEN_RETURNS);

    const {
      marketplace: {
        account: { multiBoxReturns }
      }
    } = this.context;

    if (!martyReturns || !multiBoxReturns) {
      this.context.router.push('/account');
    }

    if (!orders?.length) {
      fetchOrders({}, '/returns');
    }

    resetReturnsInState(); // when user clicks back button for example, clear returns state
    updateBoxNumInfo(1, boxInfo, items); // initialize 1 box default in store (selected from order history)
  }

  onReturnChecked = ({ item, orderId }) => {
    const {
      account: { cancelOrReturnItems: items },
      orders: { orders },
      selectItemForCancellationOrReturn,
      removeItemForCancellationOrReturn
    } = this.props;

    const { itemUniqueId } = item;

    const order = orders.filter(order => order.orderId === orderId);
    const orderItem = order[0].lineItems.filter(item => item.itemUniqueId === itemUniqueId);
    // if it exists in return items, remove, otherwise, add

    if (items.find(item => item.itemUniqueId === orderItem[0].itemUniqueId)) {
      removeItemForCancellationOrReturn(orderItem[0]);
    } else {
      orderItem[0].orderId = orderId;
      selectItemForCancellationOrReturn(orderItem[0]);
    }
  }

  onCancelReturn = () => {
    this.context.router.pushPreserveAppRoot('/account');
  }

  onNavigateToBoxSelection = () => {
    const {
      account: { cancelOrReturnItems: items },
      returns: { boxInfo },
      stepSelection,
      updateBoxNumInfo
    } = this.props;
    updateBoxNumInfo(1, boxInfo, items); // initialize 1 box default in store (navigated from 'Return Items')
    window.scrollTo(0, 0);
    if (items.length === 1) {
      stepSelection('SELECT_LABEL');
      this.context.router.pushPreserveAppRoot('/return');
    } else {
      stepSelection('SELECT_BOX');
    }
  }

  onBoxNumSelect = e => {
    const {
      account: { cancelOrReturnItems: items },
      returns: { boxInfo },
      updateBoxNumInfo
    } = this.props;
    const boxNum = +e.target.value;
    this.setState({ boxNum });
    updateBoxNumInfo(boxNum, boxInfo, items);
  }

  onProductBoxNumSelect = e => {
    const { returns: { boxInfo }, updateItemInBoxInfo } = this.props;
    // value in option is {boxNumSelected}-{itemUniqueId}
    // The regex says "split using just the first hyphen."
    const boxNumAndItemId = e.target.value.split(/([^-]*)-(.*)/);
    const boxNum = boxNumAndItemId[1];
    const itemId = boxNumAndItemId[2];
    updateItemInBoxInfo(boxNum, itemId, boxInfo);
  }

  onNavigateToReturnInitiate = () => {
    const { stepSelection, returns: { boxInfo } } = this.props;
    let boxesValid = true;
    for (const box in boxInfo) {
      if (!boxInfo[box].items.length) {
        boxesValid = false;
        break;
      }
    }
    if (boxesValid) {
      stepSelection('SELECT_LABEL');
      this.context.router.pushPreserveAppRoot('/return');
    } else {
      // show error messaging that boxes aren't valid
    }
  }

  makeBoxSelection = () => {
    const { account: { cancelOrReturnItems: items = [] } } = this.props;
    const maxBoxes = (items.length > RETURN_MAX_BOXES) ? RETURN_MAX_BOXES : items.length;
    const options = [];

    for (let i = 1; i <= maxBoxes; i++) {
      options.push(<option key={i} value={i}>{i} {pluralize('Box', i)}</option>);
    }

    return (
      <div className={css.boxNumSelection}>
        <h1>Items you are returning</h1>
        <ul className={css.boxInfoMsg}>
          <li>Select how many boxes you&apos;re using to ship back your return.</li>
          <li>If using more than one box, select which items go in each box.</li>
        </ul>
        <h2>How many boxes will you be shipping back?</h2>
        <label htmlFor="boxSelect">Select number of boxes</label>
        <select id="boxSelect" onChange={this.onBoxNumSelect}>
          {options}
        </select>
        <h2>Please select which items go in each box</h2>
      </div>
    );
  }

  hasEmptyBoxes = () => {
    const { returns: { boxInfo = {} } } = this.props;
    let hasEmptyBoxes = false;

    Object.keys(boxInfo).forEach(num => {
      const box = boxInfo[num];
      if (!box.items?.length) {
        hasEmptyBoxes = true;
      }
    });

    return hasEmptyBoxes;
  }

  makeNoReturnItemsError = () => (
    <div className={css.noReturnItems}>
      <p>You currently don't have any returnable items made within your past 10 orders.</p>
      <p>Please navigate to your <Link to="/orders">Order History</Link> to return items in orders placed at an earlier date.</p>
    </div>
  );

  render() {
    const {
      account: { cancelOrReturnItems: items },
      returns: { step, boxInfo },
      orders: { orders },
      hydraZenReturns
    } = this.props;

    const { marketplace: { account: { hideAllReturns } } } = this.context;

    const shouldBeNonReturnable = hydraZenReturns ? !hydraZenReturns : hideAllReturns; // if we are shwoing zen returns we want the opposite, so the returnable state is viewable

    if (!orders) {
      return <PageLoader centered={true} />;
    }

    return (
      <SiteAwareMetadata>
        <div className={cn(container)}>
          <div className={cn(cols, css.wrapper)}>
            <div className={leftCol}>
              { // Return Items (across multiple orders) screen.
                // Navigated via /returns or clicking 'Return Items' on Main account (/account) page.
                step === 'SELECT_RETURN_ITEMS' &&
                (<>
                  <h1>Select items to return</h1>
                  { isOrderReturnable(orders, shouldBeNonReturnable) ? // check if orders contain returnable items
                    orders.map(order => {
                      const { lineItems, orderId, orderDate, returnable } = order;
                      return returnable && <ReturnProductCards
                        key={orderId}
                        orderDate={orderDate}
                        step={step}
                        boxNum={1}
                        onReturnChecked={this.onReturnChecked}
                        returnItems={items}
                        items={lineItems}
                        orderId={orderId} />;
                    }) : this.makeNoReturnItemsError() }
                </>)
              }
              { // Box Selection screen.
                // Navigated after selecting items from /returns or selecting items directly from Order Detail page.
                step === 'SELECT_BOX' &&
                (<>
                  {this.makeBoxSelection()}
                  <ReturnProductCards
                    step={step}
                    boxNum={this.state.boxNum}
                    items={items}
                    onProductBoxNumSelect={this.onProductBoxNumSelect} />
                </>)
              }
            </div>
            <div className={cn(rightCol, css.side)}>
              { !!items?.length && step !== 'SELECT_LABEL' && <FloatingReturnItemList
                step={step}
                items={items}
                boxInfo={boxInfo}
                hasEmptyBoxes={this.hasEmptyBoxes}
                onNavigateToBoxSelection={this.onNavigateToBoxSelection}
                onNavigateToReturnInitiate={this.onNavigateToReturnInitiate}
                onCancelReturn={this.onCancelReturn} />
              }
            </div>
          </div>
        </div>
      </SiteAwareMetadata>
    );
  }
}

function mapStateToProps(state) {
  const {
    account,
    orders,
    returns
  } = state;
  return {
    account,
    hydraZenReturns: isAssigned(HYDRA_ZEN_RETURNS, 1, state),
    returns,
    orders
  };
}

export default connect(mapStateToProps, {
  fetchOrders,
  removeItemForCancellationOrReturn,
  resetReturnsInState,
  selectItemForCancellationOrReturn,
  stepSelection,
  updateBoxNumInfo,
  updateItemInBoxInfo
})(ReturnAll);
